import { useEffect, useState } from "react";
import type { PublicEvent } from "@vedaunion/shared";
import { useClearRsvp, useRsvp } from "../../lib/events";
import { ApiError } from "../../lib/api";

/**
 * RSVP control with optional note. The note is per-user, persisted alongside
 * the status, and visible to event organisers via the rsvps panel. Capacity
 * full → server returns 409, surfaced as a friendly inline error.
 */
export function RsvpPanel({ event }: { event: PublicEvent }) {
  const rsvp = useRsvp(event.id);
  const clear = useClearRsvp(event.id);
  const [note, setNote] = useState(""); // not pre-filled — server doesn't return mine
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
  }, [event.id]);

  const set = async (status: "going" | "maybe" | "declined") => {
    setError(null);
    try {
      await rsvp.mutateAsync({ status, note: note.trim() || null });
    } catch (err) {
      const e = err as ApiError;
      setError(e?.body?.message ?? e?.message ?? "Could not update RSVP.");
    }
  };

  const onClear = async () => {
    setError(null);
    try {
      await clear.mutateAsync();
      setNote("");
    } catch (err) {
      const e = err as ApiError;
      setError(e?.body?.message ?? e?.message ?? "Could not clear RSVP.");
    }
  };

  const capacityNote = (() => {
    if (event.capacity === null) return null;
    const remaining = Math.max(0, event.capacity - event.rsvpCount);
    if (event.isFull && event.myRsvp !== "going") return "This event is at capacity.";
    if (remaining <= 5 && remaining > 0) return `${remaining} spot${remaining === 1 ? "" : "s"} left`;
    return null;
  })();

  return (
    <section className="rsvp-panel">
      <header className="rsvp-panel-head">
        <p className="page-eyebrow mb-1">Your RSVP</p>
        <p className="rsvp-panel-count">
          {event.rsvpCount} going
          {event.maybeCount > 0 && <> · {event.maybeCount} maybe</>}
          {event.capacity !== null && <> · {event.capacity} capacity</>}
        </p>
      </header>
      <div className="rsvp-buttons">
        {(["going", "maybe", "declined"] as const).map((s) => {
          const disabled = s === "going" && event.isFull && event.myRsvp !== "going";
          return (
            <button
              key={s}
              type="button"
              className={`rsvp-btn ${event.myRsvp === s ? "is-active" : ""}`}
              onClick={() => set(s)}
              disabled={rsvp.isPending || disabled}
              title={disabled ? "Event is full" : undefined}
            >
              {s}
            </button>
          );
        })}
        {event.myRsvp && (
          <button type="button" className="rsvp-btn rsvp-btn-clear" onClick={onClear} disabled={clear.isPending}>
            Clear
          </button>
        )}
      </div>
      {capacityNote && <p className="rsvp-capacity">{capacityNote}</p>}
      <label className="rsvp-note-label">
        <span>Note for the organiser (optional)</span>
        <input
          type="text"
          className="auth-input"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Dietary needs, arrival time, etc."
        />
      </label>
      {error && <p className="auth-error">— {error}</p>}
    </section>
  );
}
